// Utilitaires partagés de la Messagerie (DRY — consigne n°4)
// Utilisés à la fois par le hook useMailHub (logique) et par MailHub (rendu).

// Liste d'emojis couramment utilisés
export const EMOJI_LIST = [
  // Visages positifs
  '😀', '😃', '😄', '😁', '😊', '😇', '🙂', '😉', '😌', '😍', '🥰', '😘',
  // Visages neutres/pensifs
  '🤔', '😐', '😑', '🤨', '😏', '🙄', '😬',
  // Gestes
  '👍', '👎', '👌', '✌️', '🤞', '🤝', '👏', '🙌', '💪', '🙏',
  '👋', '✋', '👆', '👇', '👈', '👉', '☝️',
  // Cœurs
  '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '💕', '💖',
  // Validation/Statuts
  '✅', '❌', '⭐', '🌟', '💯', '🔥', '✨', '💡', '⚡',
  // Business & Pro
  '🚀', '📈', '📉', '📊', '💼', '📁', '📂', '🗂️', '📋', '📌', '📎', '🔗',
  '💰', '💵', '💶', '💳', '🏦', '📄', '📑', '📝', '✍️', '🖊️', '📆', '📅',
  '⏰', '⏳', '🕐', '📞', '📱', '💻', '🖥️', '⌨️', '🖨️', '📧', '📩', '📨',
  // Succès & Célébration
  '🎉', '🎊', '🏆', '🥇', '🥈', '🥉', '🎯', '🏅', '🎖️',
  // Alertes & Attention
  '⚠️', '🚨', '❗', '❓', '💬', '🔔', '🔕', '📢', '📣',
  // Nature & Météo
  '☀️', '🌤️', '⛅', '🌈', '🌍', '🌎', '🌏',
  // Flèches & Symboles
  '➡️', '⬅️', '⬆️', '⬇️', '↗️', '↘️', '🔄', '🔃', '▶️', '⏸️', '⏹️',
  // Divers Pro
  '🔒', '🔓', '🔑', '🛡️', '⚙️', '🔧', '📦', '🏠', '🏢', '🏗️', '🚗', '✈️'
];

/**
 * Convertit une date UTC (MongoDB) en objet Date JavaScript
 * Gère automatiquement le fuseau horaire Europe/Paris (été/hiver)
 */
export const parseUTCDate = (dateString) => {
  if (!dateString) return null;
  // Si la date n'a pas d'indicateur de fuseau (Z ou +), on ajoute Z pour indiquer UTC
  if (typeof dateString === 'string' && !dateString.includes('Z') && !dateString.includes('+')) {
    return new Date(dateString + 'Z');
  }
  return new Date(dateString);
};

/**
 * Formate une date UTC pour affichage en heure locale Paris
 * @param {string} dateString - Date au format ISO (UTC)
 * @param {object} options - Options de formatage Intl.DateTimeFormat
 */
export const formatDateParis = (dateString, options = {}) => {
  const date = parseUTCDate(dateString);
  if (!date || isNaN(date.getTime())) return '';

  const defaultOptions = {
    timeZone: 'Europe/Paris',
    ...options
  };

  return date.toLocaleString('fr-FR', defaultOptions);
};

/**
 * Formate une date pour la liste des emails (date + heure, heure locale Paris).
 * Affiche l'année sur 2 chiffres si la date est antérieure à plus d'un an.
 */
export const formatDate = (dateString) => {
  const date = parseUTCDate(dateString);
  if (!date || isNaN(date.getTime())) return '';

  const now = new Date();
  const diff = now - date;
  const hours = Math.floor(diff / (1000 * 60 * 60));

  const dateStr = date.toLocaleDateString('fr-FR', {
    timeZone: 'Europe/Paris',
    day: '2-digit',
    month: '2-digit',
    year: hours > 8760 ? '2-digit' : undefined // Année si > 1 an
  });
  const timeStr = date.toLocaleTimeString('fr-FR', {
    timeZone: 'Europe/Paris',
    hour: '2-digit',
    minute: '2-digit'
  });

  return `${dateStr} ${timeStr}`;
};

/**
 * Supprime le pixel de tracking d'un contenu HTML d'email pour éviter de
 * déclencher l'accusé d'ouverture depuis notre propre interface.
 */
export const removeTrackingPixel = (html) => {
  if (!html) return html;
  return html.replace(/<img[^>]*\/api\/admin\/mail\/track\/[^>]*>/gi, '');
};

/**
 * Échappe les caractères HTML spéciaux d'un texte brut.
 */
export const escapeHtml = (text) => {
  if (!text) return '';
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
};

/**
 * Convertit un texte brut en HTML minimal (échappe + retours à la ligne <br>).
 */
export const textToHtml = (text) => {
  if (!text) return '';
  return escapeHtml(text).replace(/\r?\n/g, '<br>');
};

/**
 * Neutralise les attributs `class` d'un HTML cité (transfert/réponse) pour éviter
 * les collisions avec les utilitaires Tailwind de l'app (ex. Bootstrap `.collapse`
 * = `visibility:collapse` qui masquerait le contenu dans l'éditeur KITT).
 * Les styles inline (`style="..."`) sont conservés → mise en forme préservée.
 */
export const stripCollisionClasses = (html) => {
  if (!html) return '';
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    doc.querySelectorAll('[class]').forEach((el) => el.removeAttribute('class'));
    return doc.body.innerHTML;
  } catch {
    return String(html).replace(/\sclass=("[^"]*"|'[^']*')/gi, '');
  }
};

/**
 * Construit le corps HTML d'une réponse/transfert en CONSERVANT la mise en forme
 * d'origine (body_html). Retombe sur body_text échappé si aucun HTML n'est disponible.
 * @param {object} email - email source (body_html / body_text)
 * @param {string} headerHtml - en-tête déjà en HTML (De/Date/Sujet)
 * @param {string} signatureHtml - signature HTML optionnelle (placée en haut pour la saisie)
 */
export const buildQuotedHtml = (email, headerHtml, signatureHtml = '') => {
  const original = email.body_html
    ? stripCollisionClasses(removeTrackingPixel(email.body_html))
    : textToHtml(email.body_text || '');
  return `<p><br></p>${signatureHtml || ''}<p><br></p><hr>${headerHtml || ''}<blockquote>${original}</blockquote>`;
};

